import { useState, useCallback } from 'react';
import { X, RotateCcw, Check, ChevronDown } from 'lucide-react';
import { useListingsStore } from '../../stores/listings';

const CONTRACT_TYPES = [
  { value: 'RENT', label: 'Affitto' },
  { value: 'SALE', label: 'Vendita' },
];

const PRICE_PRESETS_RENT = [
  { label: '< 800€', min: 0, max: 800 },
  { label: '800-1.2k', min: 800, max: 1200 },
  { label: '1.2k-1.8k', min: 1200, max: 1800 },
  { label: '> 1.8k', min: 1800, max: undefined },
];

const PRICE_PRESETS_SALE = [
  { label: '< 150k', min: 0, max: 150000 },
  { label: '150-300k', min: 150000, max: 300000 },
  { label: '300-500k', min: 300000, max: 500000 },
  { label: '> 500k', min: 500000, max: undefined },
];

const ROOM_PRESETS = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4+', value: 4 },
];

const SURFACE_PRESETS = [
  { label: '< 50', min: 0, max: 50 },
  { label: '50-80', min: 50, max: 80 },
  { label: '80-120', min: 80, max: 120 },
  { label: '> 120', min: 120, max: undefined },
];

const BATHROOM_PRESETS = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3+', value: 3 },
];

const SORT_OPTIONS = [
  { value: 'date', label: 'Più recenti' },
  { value: 'priceAsc', label: 'Prezzo ↑' },
  { value: 'price', label: 'Prezzo ↓' },
  { value: 'surface', label: 'Superficie' },
];

// Property condition (stato) - only for sales
const CONDITION_PRESETS = [
  { label: 'Nuovo', value: 'nuovo' },
  { label: 'Ristrutturato', value: 'ristrutturato' },
  { label: 'Buono', value: 'buono' },
  { label: 'Da ristrutt.', value: 'da_ristrutturare' },
];

interface MapFilterPanelProps {
  onClose: () => void;
}

export function MapFilterPanel({ onClose }: MapFilterPanelProps) {
  const { filters, setFilters, resetFilters } = useListingsStore();
  const [isApplying, setIsApplying] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Determine which price presets to show
  const isRent = filters.contractType === 'RENT' || !filters.contractType;
  const pricePresets = isRent ? PRICE_PRESETS_RENT : PRICE_PRESETS_SALE;

  // Check if a price preset is active
  const isPricePresetActive = (preset: { min: number; max: number | undefined }) => {
    return filters.minPrice === preset.min && filters.maxPrice === preset.max;
  };

  // Check if a room preset is active
  const isRoomPresetActive = (value: number) => {
    if (value === 4) {
      return filters.minRooms === 4;
    }
    return filters.minRooms === value && filters.maxRooms === value;
  };

  // Check if a surface preset is active
  const isSurfacePresetActive = (preset: { min: number; max: number | undefined }) => {
    return filters.minSurface === preset.min && filters.maxSurface === preset.max;
  };

  // Apply a single filter immediately
  const applyFilter = useCallback(async (newFilters: Record<string, any>) => {
    setIsApplying(true);
    try {
      setFilters({ ...newFilters, page: 1 });
      // The map will refetch automatically via the useEffect that watches filters
    } finally {
      setIsApplying(false);
    }
  }, [setFilters]);

  // Reset all filters
  const handleReset = useCallback(async () => {
    setIsApplying(true);
    try {
      resetFilters();
    } finally {
      setIsApplying(false);
      onClose();
    }
  }, [resetFilters, onClose]);

  // Chip component
  const Chip = ({
    label,
    isActive,
    onClick,
  }: {
    label: string;
    isActive: boolean;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={isApplying}
      className={`px-3 py-2 rounded-full text-sm font-medium transition-all ${
        isActive
          ? 'bg-blue-500 text-white'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
      } ${isApplying ? 'opacity-50' : ''}`}
    >
      {label}
    </button>
  );

  return (
    <div className="w-72 max-h-[70vh] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-2 border-b">
        <h3 className="font-semibold text-gray-900">Filtri</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            disabled={isApplying}
          >
            <RotateCcw className="h-3 w-3" />
            Reset
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            aria-label="Chiudi"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="space-y-5">
        {/* Contract Type */}
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
            Tipo
          </label>
          <div className="flex gap-2">
            <Chip
              label="Tutti"
              isActive={!filters.contractType}
              onClick={() => applyFilter({ contractType: undefined, minPrice: undefined, maxPrice: undefined })}
            />
            {CONTRACT_TYPES.map((type) => (
              <Chip
                key={type.value}
                label={type.label}
                isActive={filters.contractType === type.value}
                onClick={() => applyFilter({ contractType: type.value, minPrice: undefined, maxPrice: undefined })}
              />
            ))}
          </div>
        </div>

        {/* Price Presets */}
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
            Budget
          </label>
          <div className="flex flex-wrap gap-2">
            <Chip
              label="Tutti"
              isActive={!filters.minPrice && !filters.maxPrice}
              onClick={() => applyFilter({ minPrice: undefined, maxPrice: undefined })}
            />
            {pricePresets.map((preset) => (
              <Chip
                key={preset.label}
                label={preset.label}
                isActive={isPricePresetActive(preset)}
                onClick={() => applyFilter({ minPrice: preset.min, maxPrice: preset.max })}
              />
            ))}
          </div>
        </div>

        {/* Rooms */}
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
            Locali
          </label>
          <div className="flex gap-2">
            <Chip
              label="Tutti"
              isActive={!filters.minRooms && !filters.maxRooms}
              onClick={() => applyFilter({ minRooms: undefined, maxRooms: undefined })}
            />
            {ROOM_PRESETS.map((preset) => (
              <Chip
                key={preset.label}
                label={preset.label}
                isActive={isRoomPresetActive(preset.value)}
                onClick={() => {
                  if (preset.value === 4) {
                    applyFilter({ minRooms: 4, maxRooms: undefined });
                  } else {
                    applyFilter({ minRooms: preset.value, maxRooms: preset.value });
                  }
                }}
              />
            ))}
          </div>
        </div>

        {/* Surface */}
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
            Superficie (m²)
          </label>
          <div className="flex flex-wrap gap-2">
            <Chip
              label="Tutti"
              isActive={!filters.minSurface && !filters.maxSurface}
              onClick={() => applyFilter({ minSurface: undefined, maxSurface: undefined })}
            />
            {SURFACE_PRESETS.map((preset) => (
              <Chip
                key={preset.label}
                label={preset.label}
                isActive={isSurfacePresetActive(preset)}
                onClick={() => applyFilter({ minSurface: preset.min, maxSurface: preset.max })}
              />
            ))}
          </div>
        </div>

        {/* Bathrooms */}
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
            Bagni
          </label>
          <div className="flex gap-2">
            <Chip
              label="Tutti"
              isActive={!filters.minBathrooms}
              onClick={() => applyFilter({ minBathrooms: undefined })}
            />
            {BATHROOM_PRESETS.map((preset) => (
              <Chip
                key={preset.label}
                label={preset.label}
                isActive={filters.minBathrooms === preset.value}
                onClick={() => applyFilter({ minBathrooms: preset.value })}
              />
            ))}
          </div>
        </div>

        {/* Property Condition - only for sales */}
        {filters.contractType === 'SALE' && (
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
              Stato immobile
            </label>
            <div className="flex flex-wrap gap-2">
              <Chip
                label="Tutti"
                isActive={!(filters as any).propertyCondition}
                onClick={() => applyFilter({ propertyCondition: undefined } as any)}
              />
              {CONDITION_PRESETS.map((preset) => (
                <Chip
                  key={preset.value}
                  label={preset.label}
                  isActive={(filters as any).propertyCondition === preset.value}
                  onClick={() => applyFilter({ propertyCondition: preset.value } as any)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Sort */}
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
            Ordina per
          </label>
          <div className="flex flex-wrap gap-2">
            {SORT_OPTIONS.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                isActive={(filters as any).liveSortBy === option.value || (!(filters as any).liveSortBy && option.value === 'date')}
                onClick={() => applyFilter({ liveSortBy: option.value } as any)}
              />
            ))}
          </div>
        </div>

        {/* Advanced toggle */}
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center justify-between w-full py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <span>Altre caratteristiche</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
        </button>

        {/* Features - collapsible */}
        {showAdvanced && (
          <div className="grid grid-cols-2 gap-2 pb-2">
            {[
              { key: 'hasElevator', label: 'Ascensore' },
              { key: 'hasTerrace', label: 'Terrazzo' },
              { key: 'isFurnished', label: 'Arredato' },
              { key: 'hasGarden', label: 'Giardino' },
            ].map((feature) => (
              <label
                key={feature.key}
                className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer transition-all text-sm ${
                  (filters as any)[feature.key]
                    ? 'bg-blue-50 border border-blue-300'
                    : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                } ${isApplying ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={(filters as any)[feature.key] || false}
                  onChange={(e) => applyFilter({ [feature.key]: e.target.checked || undefined })}
                  className="sr-only"
                  disabled={isApplying}
                />
                <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
                  (filters as any)[feature.key]
                    ? 'bg-blue-500 text-white'
                    : 'bg-white border border-gray-300'
                }`}>
                  {(filters as any)[feature.key] && (
                    <Check className="w-3 h-3" />
                  )}
                </span>
                <span className="text-gray-700">{feature.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Apply button for mobile */}
      <div className="mt-5 pt-4 border-t md:hidden">
        <button
          type="button"
          onClick={onClose}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 active:bg-blue-700 transition-colors"
        >
          <Check className="w-4 h-4" />
          Applica filtri
        </button>
      </div>
    </div>
  );
}
